import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config';
import { 
  FaUser, 
  FaShoppingBag, 
  FaHeart, 
  FaMapMarkerAlt, 
  FaCreditCard,
  FaBell,
  FaCog,
  FaSignOutAlt,
  FaHistory,
  FaStar,
  FaBox,
  FaTruck,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import './ProfileDashboard.css';

const ProfileDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    pendingOrders: 0,
    deliveredOrders: 0
  });

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('auth-token');
    const userName = localStorage.getItem('user-name');
    const userEmail = localStorage.getItem('user-email');

    if (!token) {
      navigate('/login');
      return;
    }

    // Set user data
    setUser({
      name: userName || 'NepliShop User',
      email: userEmail || 'user@neplishop.com',
      joinDate: localStorage.getItem('join-date') || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    });

    // Fetch user orders and stats
    fetchUserData(token);
  }, [navigate]);

  const fetchUserData = async (token) => {
    setLoading(true);
    try {
      // Fetch orders
      const ordersResponse = await fetch(`${API_BASE_URL}/api/orders/history`, {
        headers: {
          'auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        if (ordersData.success) {
          setOrders(ordersData.orders || []);
          
          // Calculate stats
          const totalOrders = ordersData.orders.length;
          const totalSpent = ordersData.orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
          const pendingOrders = ordersData.orders.filter(order => 
            ['pending', 'confirmed', 'shipped'].includes(order.orderStatus)
          ).length;
          const deliveredOrders = ordersData.orders.filter(order => 
            order.orderStatus === 'delivered'
          ).length;

          setStats({
            totalOrders,
            totalSpent,
            pendingOrders,
            deliveredOrders
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('user-name');
    localStorage.removeItem('user-email');
    navigate('/login');
    window.location.reload();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return '#ff9800';
      case 'confirmed': return '#2196f3';
      case 'shipped': return '#9c27b0';
      case 'delivered': return '#4caf50';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  // Overview Tab Content
  const OverviewTab = () => (
    <div className="overview-content">
      {/* Welcome Card */}
      <div className="welcome-card">
        <div className="welcome-content">
          <h2>{getGreeting()}, {user?.name}! 👋</h2>
          <p className="welcome-message">
            Welcome back to your NepliShop dashboard. Here's what's happening with your fashion journey.
          </p>
          <div className="membership-badge">
            <FaStar className="badge-icon" />
            <span>NepliShop Premium Member</span>
          </div>
        </div>
        <div className="welcome-image">
          <div className="profile-avatar">
            <FaUser />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon orders">
            <FaShoppingBag />
          </div>
          <div className="stat-content">
            <h3>{stats.totalOrders}</h3>
            <p>Total Orders</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon spent">
            <FaCreditCard />
          </div>
          <div className="stat-content">
            <h3>Rs {stats.totalSpent.toLocaleString()}</h3>
            <p>Total Spent</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending">
            <FaTruck />
          </div>
          <div className="stat-content">
            <h3>{stats.pendingOrders}</h3>
            <p>Active Orders</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon delivered">
            <FaBox />
          </div>
          <div className="stat-content">
            <h3>{stats.deliveredOrders}</h3>
            <p>Delivered</p>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="recent-orders">
        <div className="section-header">
          <h3>📋 Recent Orders</h3>
          <button 
            className="view-all-btn"
            onClick={() => setActiveTab('orders')}
          >
            View All →
          </button>
        </div>
        
        {loading ? (
          <div className="loading-orders">
            <div className="spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="no-orders">
            <FaShoppingBag className="no-orders-icon" />
            <h4>No orders yet</h4>
            <p>Start shopping to see your order history here</p>
            <button 
              className="shop-now-btn"
              onClick={() => navigate('/')}
            >
              🛍️ Shop Now
            </button>
          </div>
        ) : (
          <div className="orders-list-mini">
            {orders.slice(0, 3).map((order) => (
              <div key={order._id || order.orderId} className="order-item-mini">
                <div className="order-info">
                  <h4>Order #{order.orderId || order._id}</h4>
                  <p className="order-date">{formatDate(order.orderDate)}</p>
                </div>
                <div className="order-details-mini">
                  <span className="order-amount">Rs {order.totalAmount}</span>
                  <span 
                    className="order-status-badge"
                    style={{ backgroundColor: getStatusColor(order.orderStatus) }}
                  >
                    {order.orderStatus}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>⚡ Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-btn" onClick={() => navigate('/shop')}>
            <FaShoppingBag />
            <span>Continue Shopping</span>
          </button>
          <button className="action-btn" onClick={() => setActiveTab('wishlist')}>
            <FaHeart />
            <span>View Wishlist</span>
          </button>
          <button className="action-btn" onClick={() => setActiveTab('address')}>
            <FaMapMarkerAlt />
            <span>Manage Addresses</span>
          </button>
          <button className="action-btn" onClick={() => setActiveTab('settings')}>
            <FaCog />
            <span>Account Settings</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Orders Tab Content
  const OrdersTab = () => {
    const [expandedOrder, setExpandedOrder] = useState(null);

    const toggleOrderDetails = (orderId) => {
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    return (
      <div className="orders-tab">
        <div className="section-header">
          <h2>📋 Your Order History</h2>
          <p>Track and manage all your orders in one place</p>
        </div>

        {loading ? (
          <div className="loading-orders">
            <div className="spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="no-orders-full">
            <FaShoppingBag className="no-orders-icon-large" />
            <h3>No orders yet</h3>
            <p>Your order history will appear here once you place an order</p>
            <button 
              className="shop-now-btn-large"
              onClick={() => navigate('/')}
            >
              🛍️ Start Shopping
            </button>
          </div>
        ) : (
          <div className="orders-list-full">
            {orders.map((order) => (
              <div key={order._id || order.orderId} className="order-card-full">
                <div 
                  className="order-header-full"
                  onClick={() => toggleOrderDetails(order._id || order.orderId)}
                >
                  <div className="order-basic-info">
                    <div className="order-id-date">
                      <h4>Order #{order.orderId || order._id}</h4>
                      <span className="order-date">{formatDate(order.orderDate)}</span>
                    </div>
                    <div className="order-amount-full">
                      Rs {order.totalAmount}
                    </div>
                  </div>
                  <div className="order-status-section">
                    <span 
                      className="order-status-badge-full"
                      style={{ backgroundColor: getStatusColor(order.orderStatus) }}
                    >
                      {order.orderStatus}
                    </span>
                    <span className={`expand-icon ${expandedOrder === (order._id || order.orderId) ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  </div>
                </div>

                {expandedOrder === (order._id || order.orderId) && (
                  <div className="order-details-full">
                    <div className="order-items-full">
                      <h5>Items Ordered:</h5>
                      {order.items?.map((item, index) => (
                        <div key={index} className="order-item-full">
                          <img 
                            src={item.image || item.main_image} 
                            alt={item.name}
                            onError={(e) => {
                              e.target.src = '/images/placeholder.jpg';
                            }}
                          />
                          <div className="item-details">
                            <h6>{item.name}</h6>
                            <p>Size: {item.size || 'One Size'}</p>
                            <p>Quantity: {item.quantity}</p>
                            <p>Price: Rs {item.price} each</p>
                          </div>
                          <div className="item-total">
                            Rs {item.price * item.quantity}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="order-meta-info">
                      <div className="shipping-info">
                        <h5>Shipping Address:</h5>
                        <p><strong>{order.shippingInfo?.name}</strong></p>
                        <p>{order.shippingInfo?.street}, {order.shippingInfo?.city}</p>
                        <p>{order.shippingInfo?.district}, {order.shippingInfo?.postalCode}</p>
                        <p>📞 {order.shippingInfo?.phone}</p>
                      </div>

                      <div className="payment-info">
                        <h5>Payment Details:</h5>
                        <p><strong>Method:</strong> {order.paymentMethod}</p>
                        <p><strong>Status:</strong> 
                          <span className={`payment-status ${order.paymentStatus}`}>
                            {order.paymentStatus}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Other tabs (simplified for now)
  const WishlistTab = () => (
    <div className="tab-content">
      <h2>❤️ Your Wishlist</h2>
      <p className="coming-soon">Coming Soon! Your saved items will appear here.</p>
    </div>
  );

  const AddressTab = () => (
    <div className="tab-content">
      <h2>📍 Saved Addresses</h2>
      <p className="coming-soon">Manage your delivery addresses (Coming Soon)</p>
    </div>
  );

  const SettingsTab = () => (
    <div className="tab-content">
      <h2>⚙️ Account Settings</h2>
      <p className="coming-soon">Update your profile and preferences (Coming Soon)</p>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab />;
      case 'orders':
        return <OrdersTab />;
      case 'wishlist':
        return <WishlistTab />;
      case 'address':
        return <AddressTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <OverviewTab />;
    }
  };

  if (!user) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-dashboard">
      {/* Sidebar */}
      <div className="profile-sidebar">
        <div className="user-info">
          <div className="user-avatar">
            <FaUser />
          </div>
          <div className="user-details">
            <h3>{user.name}</h3>
            <p className="user-email">{user.email}</p>
            <p className="member-since">Member since {user.joinDate}</p>
          </div>
        </div>

        <nav className="profile-nav">
          <button 
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaUser className="nav-icon" />
            <span>Overview</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <FaHistory className="nav-icon" />
            <span>Order History</span>
            {stats.totalOrders > 0 && (
              <span className="nav-badge">{stats.totalOrders}</span>
            )}
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'wishlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('wishlist')}
          >
            <FaHeart className="nav-icon" />
            <span>Wishlist</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            <FaMapMarkerAlt className="nav-icon" />
            <span>Addresses</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <FaCog className="nav-icon" />
            <span>Settings</span>
          </button>

          <div className="nav-divider"></div>

          <button 
            className="nav-item logout"
            onClick={handleLogout}
          >
            <FaSignOutAlt className="nav-icon" />
            <span>Logout</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <p className="support-text">Need help?</p>
          <button className="support-btn">
            Contact Support
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="profile-main">
        <div className="profile-header">
          <div className="header-left">
            <h1>{activeTab === 'overview' ? 'My Dashboard' : 
                  activeTab === 'orders' ? 'Order History' :
                  activeTab === 'wishlist' ? 'My Wishlist' :
                  activeTab === 'address' ? 'Saved Addresses' : 'Account Settings'}</h1>
            <p className="header-subtitle">
              {activeTab === 'overview' ? 'Welcome to your NepliShop dashboard' :
               activeTab === 'orders' ? 'Track and manage all your orders' :
               activeTab === 'wishlist' ? 'Your saved fashion items' :
               activeTab === 'address' ? 'Manage your delivery addresses' : 'Update your account preferences'}
            </p>
          </div>
          <div className="header-actions">
            <button className="notification-btn">
              <FaBell />
              <span className="notification-badge">3</span>
            </button>
            <button className="help-btn">
              Need Help?
            </button>
          </div>
        </div>

        <div className="profile-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ProfileDashboard;

